
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService, MsalBroadcastService, } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { LoginProviderEnum } from 'src/app/core/enums/login-provider-enum';
import { LoginModel } from 'src/app/core/models/login.model';
import { LoginType } from 'src/app/core/models/members/user/user-context.model';
import { SettingsModel } from 'src/app/core/models/system/settings.model';
import { AuthMyfileService } from 'src/app/core/services/auth.myfile.service';
import { MyfileApiService } from 'src/app/core/services/myfile-api.service';
import { OAuthRedirectTokenizerService } from 'src/app/core/services/oauth-redirect-tokenizer.service';
import { ToasterNotificationService } from 'src/app/core/services/toaster-notification.service';
import { GlobalComponent } from 'src/app/global-component'
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {

  // socialUser!: SocialUser;
  // loggedIn!: boolean;
  // public user: SocialUser = new SocialUser();

  // Login Form
  loginForm!: UntypedFormGroup;
  submitted = false;
  fieldTextType!: boolean;
  error = '';
  returnUrl!: string;
  // set the current year
  year: number = new Date().getFullYear();
  defaultAuth: LoginModel = new LoginModel;
  isLoading: boolean = false;
  tenantSelectOptions: { name: string, tenantId: string, isDefault: boolean }[] = []
  showTenants: boolean = false;
  passwordCard: boolean = false;
  show2fACard: boolean = false;
  expiry2FADate: Date | undefined = undefined;

  redirectURL: string | undefined = undefined;

  settingsModel: SettingsModel = new SettingsModel();
  oAuthLogin: boolean = false;

  LoginProvider = LoginProviderEnum;

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  @Input() loginType: LoginType = LoginType.Client;

  LoginType = LoginType;
  _destroying$ = new Subject<void>();

  constructor(
    protected toasterNotificationService: ToasterNotificationService,
    protected myfileApiService: MyfileApiService,
    private formBuilder: UntypedFormBuilder,
    protected router: Router,
    protected route: ActivatedRoute,
    protected decodeTokenService: OAuthRedirectTokenizerService,
    // private socialAuthServiceConfig: SocialAuthService,
    private msalAuthService: MsalService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private broadcastService: MsalBroadcastService,
    protected authService: AuthMyfileService,) {
    if (this.authService.currentUserContextValue) {
      this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {

    this.route.queryParams.subscribe(params => {
      this.redirectURL = params['redirectUrl'];
      if (this.redirectURL != undefined) {
        this.redirectURL = decodeURIComponent(this.redirectURL);
      }
    })

    this.initForm();
    this.myfileApiService.getSystemPublicSettings().subscribe((result: SettingsModel) => {
      if (result && result.locked) {

      } else {
        //added a checkVersion bool to make sure that we can disable infinite page reloads if their is a version error during deployment
        if (result.uxVersion && (result.checkVersion === true)) {
          if (result.uxVersion != environment.appVersion) {
            window.location.reload();
          }
        }

        this.settingsModel = result;

        if (this.loginType == LoginType.Client) {
          this.returnUrl = GlobalComponent.authRedirectUrls.clientSignIn;
        } else {
          this.returnUrl = GlobalComponent.authRedirectUrls.userSignIn;
        }

        const subscr = this.authService.isLoading$
          .subscribe((val: boolean) => {
            this.isLoading = val;
          });
        this.unsubscribe.push(subscr);
        this.msalAuthService.initialize();

        this.broadcastService.inProgress$
          .pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            takeUntil(this._destroying$)
          )
          .subscribe(() => {
            //success log in
          })
      }
    })

  }

  anyProvidersExist(): boolean {
    if (this.settingsModel && this.settingsModel.identityProviders && (this.settingsModel.identityProviders.appleSignIn || this.settingsModel.identityProviders.azureSignIn || this.settingsModel.identityProviders.facebookSignIn || this.settingsModel.identityProviders.googleSignIn)) {
      return true;
    }
    return false;
  }

  // convenience getter for easy access to form fields
  get f() { return this.loginForm.controls; }

  async msLogin() {
    await this.msalAuthService.initialize().subscribe(async result => {

      try {
        this.msalAuthService.logoutPopup;
        var loginPopup = this.msalAuthService.loginPopup({
          scopes: [],
          prompt: 'create',
        });
        var sub = loginPopup.subscribe((response: AuthenticationResult) => {
          this.msalAuthService.instance.setActiveAccount(response.account);
          this.submitted = true;
          this.oAuthLogin = true;
          this.authService.getUserFromTenantList(response.account.username!, this.loginType).subscribe(result => {
            if (result) {
              this.tenantSelectOptions = result;
              this.defaultAuth.socialId = response.uniqueId;
              this.defaultAuth.email = response.account.username;
              this.defaultAuth.password = response.uniqueId;
              this.defaultAuth.loginProvider = LoginProviderEnum.AzureActiveDirectory;

              if (this.tenantSelectOptions.length > 1) {
                this.passwordCard = true;
                this.oAuthLogin = true;
                this.showTenants = true;
                this.tenantSelectOptions.forEach(tenant => {
                  if (tenant.isDefault) {
                    this.defaultAuth.tenantId = tenant.tenantId;
                  }
                })
              } else {
                if (this.tenantSelectOptions[0]) {
                  this.defaultAuth.tenantId = this.tenantSelectOptions[0].tenantId;
                  this.loginWithTenant(this.defaultAuth.loginProvider);
                }
              }
            }
            sub.unsubscribe();
            this.submitted = false;
          })
        }, (error: any) => {
          this.msalAuthService.instance.clearCache();
          sub.unsubscribe();
        });
      } catch (error) {
        console.log(error)
      }
    });
  }

  initForm() {
    this.loginForm = this.formBuilder.group({
      email: [
        this.defaultAuth.email,
        Validators.compose([
          Validators.required,
          Validators.pattern(GlobalComponent.emailPattern),
          Validators.minLength(3),
          Validators.maxLength(50),
        ]),
      ]
    });
  }

  onSubmit() {
    this.submitted = true;
    this.oAuthLogin = false;
    this.defaultAuth.loginProvider = LoginProviderEnum.Native;
    if (this.loginForm.invalid) {
      return;
    }
    this.defaultAuth.email = this.f['email'].value;
    this.authService.getUserFromTenantList(this.defaultAuth.email!, this.loginType).subscribe(result => {
      if (result) {
        this.passwordCard = true;
        this.tenantSelectOptions = result;
        if (this.tenantSelectOptions[0]) {
          this.defaultAuth.tenantId = this.tenantSelectOptions[0].tenantId;
        }
        if (this.tenantSelectOptions.length > 1) {
          this.showTenants = true;
          this.tenantSelectOptions.forEach(tenant => {
            if (tenant.isDefault) {
              this.defaultAuth.tenantId = tenant.tenantId;
            }
          })
        }
      }
      this.submitted = false;
    })
  }

  /**
   * Password Hide/Show
   */
  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
  resendCode() {
    this.submitted = true;

    if (this.defaultAuth.loginProvider != undefined || (this.defaultAuth.password != undefined && this.defaultAuth.password != "")) {
      const loginSubscr = this.authService
        .login(this.defaultAuth.email!, this.defaultAuth.password!, this.loginType, this.defaultAuth.tenantId!, this.defaultAuth.socialId, this.defaultAuth.loginProvider)
        .subscribe((user: any) => {
          if (user && user.isMultiFactorAuthenticationEnabled) {
            this.expiry2FADate = new Date(user.expiryDate);
            this.toasterNotificationService.showInfo('Your authentication code has been resent!', 'Success');
          } else {
            this.toasterNotificationService.showError('Authentication code could not be send.', 'Error');
          }
        });
      this.unsubscribe.push(loginSubscr);
    } else {
      this.submitted = false;
    }
  }

  loginWithTenant(loginProvider?: LoginProviderEnum) {
    this.submitted = true;

    if (this.oAuthLogin) {
      loginProvider = this.defaultAuth.loginProvider;
    }
    if (loginProvider != undefined || (this.defaultAuth.password != undefined && this.defaultAuth.password != "")) {
      const loginSubscr = this.authService
        .login(this.defaultAuth.email!, this.defaultAuth.password!, this.loginType, this.defaultAuth.tenantId!, this.defaultAuth.socialId, this.defaultAuth.loginProvider, this.defaultAuth.accessCode)
        .subscribe((user: any) => {
          if (user && user.id) {
            localStorage.setItem('toast', 'true');
            if (this.redirectURL) {
              this.redirectURL = decodeURIComponent(this.redirectURL);
              this.router.navigateByUrl(this.redirectURL);
            } else {
              this.router.navigate([`${GlobalComponent.landingPage}`]);
            }
          } else if (user && user.isMultiFactorAuthenticationEnabled) {
            this.expiry2FADate = new Date(user.expiryDate);
            this.show2fACard = true;
            this.passwordCard = false;
            this.submitted = false;
          } else {
            if (this.redirectURL) {
              this.redirectURL = decodeURIComponent(this.redirectURL);
              this.router.navigateByUrl(this.redirectURL);
            } else {
              this.router.navigate([`${GlobalComponent.landingPage}`]);
            }
          }
        });
      this.unsubscribe.push(loginSubscr);
    } else {
      this.submitted = false;
    }
  }

  stringLengthValidation(input: string | undefined): number {
    if (input == undefined) {
      return 0;
    } else {
      return input.length
    }
  }

  clearData() {
    this.loginForm.reset();
    this.fieldTextType = false
    this.defaultAuth = new LoginModel;
    this.showTenants = false;
    this.passwordCard = false;
    this.show2fACard = false;
    this.submitted = false;
  }

  oAuthSignInWithLoginProvider(loginProviderEnum: LoginProviderEnum) {
    this.decodeTokenService.setLoginType(this.loginType);
    switch (loginProviderEnum) {
      case LoginProviderEnum.Google:
        window.location.href = environment.oAuthUrls.signIn.googleOauth;
        break;
      case LoginProviderEnum.Facebook:
        window.location.href = environment.oAuthUrls.signIn.facebookOauth;
        break;
      case LoginProviderEnum.Apple:
        window.location.href = environment.oAuthUrls.signIn.appleOauth;
        break;
    }
  }

}